/**
 * useYearWheelNotifications Hook
 * Monitors year wheel tasks and sends notifications 7 days before due date
 */

import { useEffect } from 'react';
import { useSettingsStore } from '@/stores/settings-store';
import { useAuthStore } from '@/stores/auth-store';
import { apiGet } from '@/services/api-client';
import { useNotifications } from './useNotifications';

interface YearWheelTask {
  id: string;
  title: string;
  nextDue?: string;
  lastCompleted?: string | null;
  enabled?: boolean;
}

export function useYearWheelNotifications() {
  const { sendNotification, permissionStatus } = useNotifications();
  const { notifications: notificationSettings } = useSettingsStore();
  const { isAuthenticated, user } = useAuthStore();

  useEffect(() => {
    // In the native mobile apps the backend sends Expo/OS push notifications.
    // Avoid also scheduling a local WebView notification for the same event.
    if (typeof window !== 'undefined' && window.ReactNativeWebView) {
      return;
    }

    if (!notificationSettings.yearWheelReminders || !permissionStatus.granted || !isAuthenticated) {
      return;
    }

    // Check for upcoming tasks once per day at 8 AM
    const now = new Date();
    const nextCheck = new Date();
    nextCheck.setHours(8, 0, 0, 0);
    
    // If it's past 8 AM today, schedule for tomorrow
    if (now.getHours() >= 8) {
      nextCheck.setDate(nextCheck.getDate() + 1);
    }

    const msUntilNextCheck = nextCheck.getTime() - now.getTime();

    const checkUpcomingTasks = async () => {
      try {
        const familyId = user?.familyId;
        const endpoint = familyId
          ? `/year-wheel?familyId=${encodeURIComponent(familyId)}`
          : '/year-wheel';
        const response = await apiGet<YearWheelTask[]>(endpoint);

        if (!response.success || !Array.isArray(response.data)) return;

        const tasks = response.data;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Find tasks that should trigger notification exactly 7 days before their deadline.
        const upcomingTasks = tasks.filter(task => {
          if (!task.nextDue || !task.enabled) return false;
          
          const dueDate = new Date(task.nextDue);
          dueDate.setHours(0, 0, 0, 0);

          const daysUntilDue = Math.round(
            (dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
          );

          return daysUntilDue === 7;
        });

        if (upcomingTasks.length > 0) {
          // Sort by due date and get the closest one
          upcomingTasks.sort((a, b) => {
            const dateA = new Date(a.nextDue!).getTime();
            const dateB = new Date(b.nextDue!).getTime();
            return dateA - dateB;
          });

          const nextTask = upcomingTasks[0];
          const dueDate = new Date(nextTask.nextDue!);
          const daysUntil = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          
          let timeText = '';
          if (daysUntil === 0) {
            timeText = 'idag';
          } else if (daysUntil === 1) {
            timeText = 'imorgon';
          } else {
            timeText = `om ${daysUntil} dagar`;
          }

          const moreText = upcomingTasks.length > 1 ? ` (och ${upcomingTasks.length - 1} till)` : '';

          await sendNotification({
            title: 'Årshjulet',
            body: `${nextTask.title} ska göras ${timeText}${moreText}`,
            data: {
              type: 'yearwheel',
              taskId: nextTask.id,
              count: upcomingTasks.length,
              url: '/app/year-wheel',
            },
          });
        }
      } catch (error) {
        console.error('[useYearWheelNotifications] Failed to check year wheel tasks:', error);
      }
    };

    // Schedule initial check
    const timeoutId = setTimeout(() => {
      checkUpcomingTasks();
      
      // Then check daily
      const intervalId = setInterval(checkUpcomingTasks, 24 * 60 * 60 * 1000);
      
      return () => {
        clearInterval(intervalId);
      };
    }, msUntilNextCheck);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [notificationSettings.yearWheelReminders, permissionStatus.granted, isAuthenticated, sendNotification, user?.familyId]);
}
